import { Getters } from "@/store/getters"
import { store } from "@/store/store"

export const suggestionsForAi = {
  AI_INSIGHTS_ON_SPRINT_REVIEW: [
    "How did this sprint perform?",
    "What didn’t go as planned?",
    "What slowed the team down?",
    "What should we improve next Sprint?"
  ],
  AI_RETROSPECTIVE_INSIGHTS: [
    "What's Cycle Time?",
    "Tell me about Agile.",
    "What is this team's WIP limit?",
    "Are we overcommitting every sprint?"
  ],
  PROJECT_LIST: [
    "Which projects need my attention?",
    "Which projects are at risk of missing their milestones?",
    "What changed across my projects this week?",
    "Where are teams getting stuck?"
  ],
  SPRINT_TILES: [
    "Which sprints need my attention?",
    "Which sprints are at risk?",
    "What is blocking Teams right now?",
    "Which teams are overloaded?"
  ],
  SPRINT_OVERVIEW: [
    "What is putting this Sprint at risk?",
    "What work is taking longer than expected?",
    "What changed since the Sprint started?"
  ],
  SPRINT_OVERVIEW_SCRUM: [
    "What's our Sprint Goal?",
    "Will we finish planned Sprint work on time?",
    "What is putting this Sprint at risk?",
    "What work is taking longer than expected?",
    "What changed since the Sprint started?"
  ],
  ALL_SPRINTS: [
    "How is the Team performing over time?",
    "What trends do you see across recent Sprints?",
    "Is the team becoming more predictable?"
  ],
  SPRINT_ANALYTICS: [
    "What's Discovered Work?",
    "What's the difference between ToDo's and Backlog Items?",
    "What does Carried Forward mean?"
  ],
  ACCOUNT_SETTINGS: [
    "Which projects are active in this account?",
    "Are any account settings limiting collaboration?",
    "Which teams need an owner or role update?"
  ],
  AI_ACTIVITY_LOG: [
    "Which AI requests are used most often?",
    "How has AI usage changed this week?",
    "Which projects or accounts generate the most AI activity?"
  ],
  MY_WORK: [
    "What should I work on today first?",
    "What changed in my assigned work this week?",
    "Am I carrying too much work across teams?"
  ],
  ITEM_DETAILS: [
    "Give me a high level item summary",
    "What is this item trying to achieve?",
    "What could block this item?",
    "Is this item small enough to complete in one Sprint?",
    "What should happen next for this item?"
  ],
  TEAM_CALENDAR: [
    "Who is unavailable during the next Sprint?",
    "Do we have enough capacity for upcoming work?",
    "Which absences overlap important deadlines?",
    "What work should we adjust because of upcoming time off?"
  ],
  ROADMAP: [
    "How do current Epics support our Goals?",
    "What changed on the roadmap recently?",
    "What should we prioritize to stay on track?"
  ],
  IDEAS: [
    "Which proposed ideas align with our current Product Goal?",
    "Which ideas have the strongest supporting evidence?",
    "Which of the listed ideas are ready for the backlog?"
  ],
  PAST_GOALS: [
    "What were the past 6 months goals?",
    "Give me a list of past year completed goals."
  ],
  USER_PROFILE: ["Advise on any user settings I can benefit from in my account"],
  TEAM_SETTINGS: [
    "Who is on this team and what are their roles?",
    "Is this team missing any key roles?",
    "Which projects is this team assigned to?",
    "How does this team's Sprint setup affect planning?"
  ],
  PROJECT_OVERVIEW: [
    "What is the current status of this project?",
    "Which Milestones or Epics need attention?",
    "What changed in this project this week?",
    "Which teams are blocked?",
    "What is most likely to delay the next delivery?"
  ],
  PROJECT_SETTINGS: [
    "Give me a list of all Teams working on this project.",
    "Which quality gates apply to this project?",
    "What should I check before changing project settings?"
  ],
  EPICS: [
    "Which Epics have the most unfinished work?",
    "What changed across Epics this week?",
    "Which Epics are blocked by dependencies?",
    "Which Epic should we prioritize next?"
  ],
  EPIC_DETAILS: [
    "What outcome is this Epic meant to deliver?",
    "How much work remains in this Epic?",
    "What changed since this Epic started?",
    "Is this Epic on track for its Milestone?"
  ],
  SPRINT_SETUP: [
    "Is this Sprint plan realistic for the team's capacity?",
    "What is the Sprint Goal?",
    "Which planned items still need estimates?",
    "Does anyone have too much assigned work?",
    "What should we adjust in this Sprint plan?"
  ],
  MILESTONES: [
    "What work remains before the end of the Milestone?",
    "Are there any dependencies that could delay delivery?",
    "What should we address to meet the Milestone delivery timeline?",
    "Which Epics are associated with current Milestone?"
  ],
  RETROSPECTIVES: [
    "What themes recur across recent Retrospectives?",
    "Are the same blockers appearing in multiple consecutive Retros?",
    "Any sentiment you can detect from the retro notes?"
  ],
  PRODUCT_BACKLOG: [
    "What work supports our current Goals?",
    "Which items have stayed in the backlog too long?",
    "What should we prioritize next?"
  ],
  TEAM_BACKLOG: [
    "Which items are aging without progress?",
    "Is the team taking on more work than it can finish?",
    "What should move into the next Sprint?",
    "Is there any work in the top 20 in the Team Backlog that is not associated with current Product Goal?"
  ],
  STORY_POKER: [
    "Which items have the widest estimate disagreement?",
    "What is a typical Story Poker participation on the Team?"
  ],
  RETRO_NOTES: [
    "What should I know about Retrospectives before adding any notes?",
    "Do my retro notes make sense?"
  ]
}

export type AiSuggestionKey = keyof typeof suggestionsForAi

/** Uses the loaded sprint to select Scrum prompts while keeping the chat modal page-agnostic. */
export function getSuggestionsForAi(key?: AiSuggestionKey): string[] {
  if (key === "SPRINT_OVERVIEW") {
    const sprint = store.getters[Getters.GET_SPRINT]
    if (sprint?.id && !sprint.continuousSprint) return suggestionsForAi.SPRINT_OVERVIEW_SCRUM
  }

  return key ? suggestionsForAi[key] : []
}
